import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { BiologyCategory, AIModel, Literature, Question } from '@shared/types'

interface AppState {
  // 选择的目录和关键词
  selectedCategory: BiologyCategory | null
  keyword: string

  // API 配置
  apiKey: string
  selectedModel: AIModel

  // 文献列表
  literatures: Literature[]
  selectedLiterature: Literature | null
  isSearchingLiterature: boolean
  searchError: string | null

  // 题目列表
  questions: Question[]
  isGeneratingQuestions: boolean
  generateError: string | null

  // LaTeX
  latexContent: string
  isConvertingLatex: boolean
  convertError: string | null
}

const initialState: AppState = {
  selectedCategory: null,
  keyword: '',
  apiKey: '',
  selectedModel: 'openai',
  literatures: [],
  selectedLiterature: null,
  isSearchingLiterature: false,
  searchError: null,
  questions: [],
  isGeneratingQuestions: false,
  generateError: null,
  latexContent: '',
  isConvertingLatex: false,
  convertError: null
}

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    // 设置目录
    setCategory: (state, action: PayloadAction<BiologyCategory>) => {
      state.selectedCategory = action.payload
    },

    // 设置关键词
    setKeyword: (state, action: PayloadAction<string>) => {
      state.keyword = action.payload
    },

    // 设置 API 配置
    setApiKey: (state, action: PayloadAction<string>) => {
      state.apiKey = action.payload
    },

    setModel: (state, action: PayloadAction<AIModel>) => {
      state.selectedModel = action.payload
    },

    // 文献检索
    searchLiteratureStart: (state) => {
      state.isSearchingLiterature = true
      state.searchError = null
      state.literatures = []
    },

    searchLiteratureSuccess: (state, action: PayloadAction<Literature[]>) => {
      state.isSearchingLiterature = false
      state.literatures = action.payload
      state.searchError = null
    },

    searchLiteratureFailure: (state, action: PayloadAction<string>) => {
      state.isSearchingLiterature = false
      state.searchError = action.payload
    },

    // 选择文献
    selectLiterature: (state, action: PayloadAction<Literature>) => {
      state.selectedLiterature = action.payload
    },

    // 题目生成
    generateQuestionsStart: (state) => {
      state.isGeneratingQuestions = true
      state.generateError = null
      state.questions = []
    },

    generateQuestionsSuccess: (state, action: PayloadAction<Question[]>) => {
      state.isGeneratingQuestions = false
      state.questions = action.payload
      state.generateError = null
    },

    generateQuestionsFailure: (state, action: PayloadAction<string>) => {
      state.isGeneratingQuestions = false
      state.generateError = action.payload
    },

    // LaTeX 转换
    convertLatexStart: (state) => {
      state.isConvertingLatex = true
      state.convertError = null
    },

    convertLatexSuccess: (state, action: PayloadAction<string>) => {
      state.isConvertingLatex = false
      state.latexContent = action.payload
      state.convertError = null
    },

    convertLatexFailure: (state, action: PayloadAction<string>) => {
      state.isConvertingLatex = false
      state.convertError = action.payload
    },

    // 重置状态
    resetLiteratures: (state) => {
      state.literatures = []
      state.selectedLiterature = null
      state.searchError = null
    },

    resetQuestions: (state) => {
      state.questions = []
      state.generateError = null
      state.latexContent = ''
    },

    resetAll: () => initialState
  }
})

export const {
  setCategory,
  setKeyword,
  setApiKey,
  setModel,
  searchLiteratureStart,
  searchLiteratureSuccess,
  searchLiteratureFailure,
  selectLiterature,
  generateQuestionsStart,
  generateQuestionsSuccess,
  generateQuestionsFailure,
  convertLatexStart,
  convertLatexSuccess,
  convertLatexFailure,
  resetLiteratures,
  resetQuestions,
  resetAll
} = appSlice.actions

export default appSlice.reducer
